/**
 * A sessão, e o portão que os três apps atravessam antes de renderizar qualquer coisa.
 *
 * O critério de aceite 1 da spec 02 é literal: *"os três recusam renderizar sem `/api/sessao`
 * válida"*. Então isto não é um hook conveniente — é o portão, e ele existe uma vez para os três,
 * porque três implementações de "está logado?" divergem e a que erra libera tela.
 *
 * **Uma chamada, no boot** (§5, regra 1). Nada renderiza antes dela, nem o esqueleto do menu: menu
 * que aparece antes da permissão pisca item que a pessoa não pode ver — e num produto onde a
 * permissão separa "vê a conversa do colega" de "não vê", esse piscar é vazamento.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { chamar, falhaDe, type FalhaDaApi } from "./api.js";

/** O formato do §5 da spec 02, servido por `GET /api/sessao` (H9). */
export interface Sessao {
  readonly usuario: { readonly id: string; readonly nome?: string | null; readonly email?: string | null };
  readonly tenant: { readonly id: string; readonly nome: string | null; readonly papel: string | null };
  readonly produto: { readonly codigo: string; readonly entitled: boolean };
  readonly permissoes: readonly string[];
  readonly plataforma: boolean;
  readonly expiraEm: number | null;
}

/**
 * O que o servidor **não** devolve hoje, e o front não pode inventar.
 *
 * O `/api/iam/introspect` do Cert4All não tem nome, e-mail nem lista de tenants (H9b). Então:
 *
 * - `usuario.nome` e `usuario.email` chegam `undefined`. A tela mostra o que tem — e uma tela que
 *   mostra o `id` é feia; uma que mostra "Fulano" inventado é **errada**.
 * - **Não existe switcher de tenant**, porque não existe lista de tenants. O §5 regra 5 fala de
 *   trocar de tenant, e essa parte da spec **não é implementável** antes do H9b. Ela está escrita
 *   aqui em vez de simplesmente ausente, para que ninguém a implemente contra um array vazio e
 *   conclua que o cliente só tem um tenant.
 */
export const LACUNAS_DA_SESSAO = ["usuario.nome", "usuario.email", "tenants[]"] as const;

export type EstadoDaSessao =
  | { readonly fase: "carregando" }
  | { readonly fase: "pronta"; readonly sessao: Sessao }
  /** 401 de verdade. O app manda para o login do Cert4All. */
  | { readonly fase: "sem_sessao" }
  /** 503 ou rede. **Não é o mesmo que `sem_sessao`** — ver `api.ts`. */
  | { readonly fase: "indisponivel"; readonly falha: FalhaDaApi }
  /** O servidor está sem IAM configurado. Só acontece em dev, e dizer isso poupa uma hora. */
  | { readonly fase: "iam_nao_configurado"; readonly mensagem: string }
  /**
   * A sessão é VÁLIDA e a pessoa não é da plataforma. **Não é `sem_sessao`.**
   *
   * A diferença não é sutil: `sem_sessao` manda entrar de novo, e entrar de novo aqui **nunca resolve** —
   * a pessoa entraria, voltaria, e leria "sua sessão terminou" outra vez, para sempre. Era exatamente o
   * que o console fazia até 2026-08-12, e apareceu abrindo as cinco telas juntas.
   */
  | { readonly fase: "sem_plataforma"; readonly nomeDoUsuario: string };

const Contexto = createContext<Sessao | null>(null);

/**
 * A sessão de dentro do app.
 *
 * **Lança** se não houver — em vez de devolver `null`. Um `null` aqui obrigaria toda tela a tratar
 * "e se não tiver sessão", que é justamente o que o portão existe para tornar impossível; e a
 * primeira tela que esquecesse o `?.` renderizaria em branco sem ninguém saber por quê.
 */
export function useSessao(): Sessao {
  const sessao = useContext(Contexto);
  if (!sessao) {
    throw new Error(
      "useSessao fora do PortaoDeSessao. Nenhuma tela deve renderizar sem sessão resolvida — " +
        "ver o critério de aceite 1 da spec 02.",
    );
  }
  return sessao;
}

/** `true` se a sessão tem a permissão. **Esconder é cortesia; permitir é do servidor** (§7.1). */
export function usePermissao(permissao: string): boolean {
  return useSessao().permissoes.includes(permissao);
}

export function buscarSessao(): Promise<Sessao> {
  return chamar<Sessao>("/api/sessao");
}

export interface PortaoDeSessaoProps {
  readonly children: ReactNode;
  /** Renderizado em cada fase que não é `pronta`. Cada app tem a sua moldura. */
  readonly aoNaoTerSessao: (estado: Exclude<EstadoDaSessao, { fase: "pronta" }>) => ReactNode;
  /**
   * Exigir `plataforma: true`.
   *
   * Ligado no console e **desligado** no painel: o §5 regra 3 diz que o painel *ignora* o campo e o
   * console *recusa* sem ele. Não é a mesma coisa que um `if` de rota, e é por isso que o console é
   * outro build noutra origem (§3).
   */
  readonly exigirPlataforma?: boolean;
}

/**
 * O portão.
 *
 * Enquanto a sessão não está `pronta`, **nada de `children` é montado** — não desabilitado, não
 * escondido com CSS: não montado. É a diferença entre uma tela que não deixa clicar e uma tela que
 * não existe, e só a segunda não vaza.
 */
export function PortaoDeSessao(props: PortaoDeSessaoProps) {
  const [estado, setEstado] = useState<EstadoDaSessao>({ fase: "carregando" });

  useEffect(() => {
    let vivo = true;

    buscarSessao().then(
      (sessao) => vivo && setEstado({ fase: "pronta", sessao }),
      (erro: unknown) => {
        if (!vivo) return;
        const falha = falhaDe(erro);

        if (falha?.tipo === "sem_sessao") return setEstado({ fase: "sem_sessao" });
        if (falha?.tipo === "indisponivel") return setEstado({ fase: "indisponivel", falha });

        // 501 do servidor sem IAM. Vira `inesperado` na classificação, e merece nome próprio: sem
        // isto, quem sobe o servidor em dev sem `CERT4ALL_API_URL` vê "erro inesperado" e vai
        // procurar bug no front.
        if (falha?.tipo === "inesperado" && falha.erro.erro === "IAM_NAO_CONFIGURADO") {
          return setEstado({
            fase: "iam_nao_configurado",
            mensagem: falha.erro.mensagem ?? "O servidor está sem CERT4ALL_API_URL/CERT4ALL_API_KEY.",
          });
        }

        // Qualquer outra falha ao resolver sessão é tratada como ausência de sessão. Fechar aberto
        // aqui seria renderizar o app inteiro sem saber quem é a pessoa.
        setEstado({ fase: "sem_sessao" });
      },
    );

    return () => {
      vivo = false;
    };
  }, []);

  if (estado.fase !== "pronta") return <>{props.aoNaoTerSessao(estado)}</>;

  // O console recusa aqui, e não numa rota: com o build separado, `plataforma: false` significa que
  // esta pessoa não deveria nem ter carregado este bundle.
  //
  // E recusa com o estado PRÓPRIO, não com `sem_sessao`: ver o comentário em `EstadoDaSessao`.
  if (props.exigirPlataforma && !estado.sessao.plataforma) {
    return (
      <>
        {props.aoNaoTerSessao({
          fase: "sem_plataforma",
          // O nome é opcional na sessão; o e-mail não some. Mostrar "entrando como <vazio>" seria pior que
          // não mostrar nada, porque a pessoa não saberia com qual conta está.
          nomeDoUsuario: estado.sessao.usuario.nome ?? estado.sessao.usuario.email ?? "você",
        })}
      </>
    );
  }

  return <Contexto.Provider value={estado.sessao}>{props.children}</Contexto.Provider>;
}

/**
 * A marca de "saiu por vontade própria".
 *
 * ## O problema que ela resolve
 *
 * A tela de entrada atende gente que chegou ali por caminhos diferentes, e até 2026-08-15 dizia a
 * mesma frase para todos: **"Sua sessão terminou"**. Para quem **clicou em Sair** isso é passivo e
 * um pouco alarmante — a sessão não terminou sozinha, a pessoa decidiu sair, e a tela responde como
 * se algo tivesse acontecido com ela.
 *
 * O caso não existia antes: até o Sair ser construído, **não havia caminho que levasse alguém ali de
 * propósito**.
 *
 * ## Por que `sessionStorage`, e por que ler apagando
 *
 * O Sair recarrega a página (é o `PortaoDeSessao` quem sabe se há sessão — ver `casca.tsx`), então
 * estado de React não sobrevive. Precisa ser algo que atravesse o `reload`:
 *
 * - **`sessionStorage` e não `localStorage`**: a marca morre com a aba. Guardá-la além disso faria a
 *   pessoa que volta amanhã, com a sessão vencida, ler "Você saiu" — a frase errada de novo, só que
 *   na outra direção.
 * - **Lida apagando** (`consumirMarcaDeSaida`): se ela ficasse, a próxima vez que a sessão vencesse
 *   **nesta mesma aba** mostraria "Você saiu" sem ninguém ter saído.
 * - **Não é token, e o §11 continua valendo.** É um booleano sobre o que a pessoa acabou de fazer.
 *   Ele não autentica nada: forjá-lo só troca uma frase.
 *
 * O `try/catch` existe porque `sessionStorage` **lança** em Safari privado e com cookies de terceiro
 * bloqueados. Falhar ali não pode impedir ninguém de sair — perde-se a frase, não a saída.
 */
const MARCA_DE_SAIDA = "hugi:saiu";

export function marcarSaida(): void {
  try {
    sessionStorage.setItem(MARCA_DE_SAIDA, "1");
  } catch {
    /* sem marca; a saída acontece do mesmo jeito */
  }
}

/**
 * Sai: apaga o cookie no servidor, marca a saída e recarrega. Devolve a falha, ou `null` se saiu.
 *
 * Um lugar só porque tem DOIS chamadores — o Sair da barra e a tela de empresa sem o Hugi, que é
 * desenhada fora da casca. Duas cópias divergiriam, e a que divergisse seria a que marca antes de o
 * servidor confirmar.
 */
export async function sairDaSessao(): Promise<string | null> {
  try {
    // `same-origin` pelo mesmo motivo do `Entrar`: é aqui que o cookie de sessão é apagado, e sem
    // isto o navegador descartaria o `Set-Cookie` da resposta — o clique "funcionaria" sem efeito.
    const r = await fetch("/api/sair", { method: "POST", credentials: "same-origin" });
    if (!r.ok) return "Não foi possível sair.";
  } catch {
    return "Sem conexão com o servidor.";
  }
  // Só DEPOIS de o servidor confirmar. Marcar antes faria a tela dizer "Você saiu" para quem não
  // saiu, se a chamada falhasse.
  marcarSaida();
  // Recarrega em vez de mexer em estado: quem sabe se há sessão é o `PortaoDeSessao`.
  window.location.reload();
  return null;
}

/** Devolve se a pessoa acabou de sair — e **apaga a marca**, para não valer duas vezes. */
export function consumirMarcaDeSaida(): boolean {
  try {
    const tinha = sessionStorage.getItem(MARCA_DE_SAIDA) === "1";
    sessionStorage.removeItem(MARCA_DE_SAIDA);
    return tinha;
  } catch {
    return false;
  }
}

/** Só para teste: monta o contexto sem passar pelo portão. */
export function SessaoDeTeste(props: { readonly sessao: Sessao; readonly children: ReactNode }) {
  return <Contexto.Provider value={props.sessao}>{props.children}</Contexto.Provider>;
}

/**
 * Uma sessão de mentira, para teste.
 *
 * `entitled: true` e sem permissão por padrão: o caminho que mais precisa de teste é o de quem
 * **não** pode, e um padrão permissivo faria cada teste de permissão começar removendo coisa.
 */
export function sessaoFalsa(parcial: Partial<Sessao> = {}): Sessao {
  return {
    usuario: { id: "u-teste" },
    tenant: { id: "t-teste", nome: "Clínica Exemplo", papel: "member" },
    produto: { codigo: "hugi", entitled: true },
    permissoes: [],
    plataforma: false,
    expiraEm: null,
    ...parcial,
  };
}

export function useMemoDaSessao<T>(fn: (sessao: Sessao) => T): T {
  const sessao = useSessao();
  return useMemo(() => fn(sessao), [sessao, fn]);
}
